
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsResult } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Product } from 'types';
import { getActiveProductsWithPrices } from '@/data/products';
import Footer from '@/components/layout/Footer';
// load Testimonials component only on client side
const Testimonials = dynamic(() => import('@/components/Testimonials'), {
    ssr: false,
});
const HeroSection = dynamic(() => import('@/components/HeroSection'));
const UserPainWorkshop = dynamic(() => import('@/components/UserPainWorkshop'));
const WorkshopFeatures = dynamic(() => import('@/components/WorkshopFeatures'));
import useLocale from 'next-translate/useTranslation';
import ContactUsWorkshop from '@/components/ContactUsWorkshop';
import ImagesWithDetailsAboutUs from '@/components/ImagesWithDetailsAboutUs';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import { scrollToSection, WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';
const BenefitsWorkshop = dynamic(() => import('@/components/BenefitsWorkshop'));
const IsThisWorkshopForMe = dynamic(() => import('@/components/IsThisWorkshopForMe'));
const FaqWorkshop = dynamic(() => import('@/components/FaqWorkshop'));
const ContactUs = dynamic(() => import('@/components/ContactUs'));
const SinglePrice = dynamic(() => import('@/components/SinglePrice'));
const LogosCloud = dynamic(() => import('@/components/LogosCloud'));
const CTAwithimagetilesWorkshop = dynamic(() => import('@/components/CTAwithimagetilesWorkshop'));
const PricingForLandingPage = dynamic(() => import('@/components/PricingForLandingPage'));
const TrainersSection = dynamic(() => import('@/components/TrainersSection'));
interface Props {
    products: Product[];
}
export default function AIWorkshops({ products }: Props) {
    const router = useRouter();
    const { t, lang } = useLocale('ai-workshops');
    const stats = [
        {
            value: 'Singapore',
            label: 'Company Based In',
            description: 'Local presence & support',
        },
        {
            value: '150+',
            label: 'Global Countries Served',
            description: 'Used globally by teams',
        },
        {
            value: '3.2M+',
            label: 'Registered Users',
            description: 'Massive scaling platform',
        },
        {
            value: '4.9/5',
            label: 'Average User Rating',
            description: 'Highly rated AI outcomes',
        },
        {
            value: 'B2C & B2B',
            label: 'Flexible Workflows',
            description: 'For freelancers & enterprises',
        },
    ];
    const parseCoverageItem = (itemKey: string, fallbackTitle: string, fallbackDesc: string) => {
        const text = t(itemKey);
        if (!text || text === itemKey)
            return { title: fallbackTitle, desc: fallbackDesc };
        const parts = text.split(':');
        if (parts.length > 1) {
            return {
                title: parts[0].trim(),
                desc: parts.slice(1).join(':').trim(),
            };
        }
        return { title: fallbackTitle, desc: text };
    };
    const coverageItems = [
        {
            ...parseCoverageItem('workshop_item_1', 'AI Personalization', 'Create and customize a professional brand voice, tailoring parameters for your targeted marketing campaigns.'),
            icon: (<svg className='h-6 w-6 text-indigo-600 dark:text-indigo-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 100-6 3 3 0 000 6z'/>
        </svg>),
        },
        {
            ...parseCoverageItem('workshop_item_2', 'Marky Agentic AI', 'Master the agentic pattern (plan → execute → review) to write code, generate websites, build presentations, and execute deep research.'),
            icon: (<svg className='h-6 w-6 text-indigo-600 dark:text-indigo-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'/>
        </svg>),
        },
        {
            ...parseCoverageItem('workshop_item_3', 'AI Transcription', 'Convert videos and audios into precise plain text and automatically generate marketing content.'),
            icon: (<svg className='h-6 w-6 text-indigo-600 dark:text-indigo-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 100-6 3 3 0 000 6z'/>
        </svg>),
        },
        {
            ...parseCoverageItem('workshop_item_4', 'Custom AI Chatbots', 'Train custom AI bots on your own files and data sources, customize their appearance, and embed them on websites.'),
            icon: (<svg className='h-6 w-6 text-indigo-600 dark:text-indigo-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z'/>
        </svg>),
        },
        {
            ...parseCoverageItem('workshop_item_5', 'AI Workflow Builder', 'Set up multi-step workflows to automate repetitive tasks and connect multiple tools.'),
            icon: (<svg className='h-6 w-6 text-indigo-600 dark:text-indigo-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012 2h6a2 2 0 012 2v2M7 7h10'/>
        </svg>),
        },
        {
            ...parseCoverageItem('workshop_item_6', 'AI Image & Video Editing', 'AI Image & Video Editing: Build stunning visual content with Kling, Veo, Seedance, Nano Banana, and generate professional headshots.'),
            icon: (<svg className='h-6 w-6 text-indigo-600 dark:text-indigo-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
          <path strokeLinecap='round' strokeLinejoin='round' d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'/>
        </svg>),
        },
    ];
    return (<main className='dark:bg-slate-950'>
      <Seo templateTitle={t('AI Workshop in Singapore: Generative and Agentic AI')} title={t('AI Workshop in Singapore | Generative and Agentic AI')} description={t('How To Make AI Work For You. Save time, boost productivity, and enhance creativity effortlessly. Gain hands-on experience with advanced 300+ AI tools.')} image={`https://easy-peasy.ai/api/og-template?title=${t('All-In-One AI Platform')}`}/>

      <div className='relative isolate overflow-hidden bg-white dark:bg-slate-950 border-b border-slate-100 dark:border-slate-900'>
        <LandingPageNavigation />

        <div className='mx-auto max-w-7xl pb-24 pt-10 sm:pb-32 lg:grid lg:grid-cols-[minmax(0,83fr)_minmax(0,117fr)] lg:gap-x-8 lg:px-8 lg:pt-12 lg:pb-20'>
          <div className='px-6 lg:px-0 lg:pt-0 lg:self-start'>
            <div className='mx-auto max-w-2xl'>
              <div className='max-w-lg'>
                <h1 className='mt-6 text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50 sm:text-6xl leading-none'>
                  <span className='block text-indigo-600 dark:text-indigo-500 text-xs sm:text-sm font-bold uppercase tracking-widest mb-4'>
                    {t('Workshop in Person')}
                  </span>
                  <span className='block text-slate-900 dark:text-slate-100'>
                    {t('How To Make AI Work For You')}
                  </span>
                </h1>

                {/* Benefits List */}
                <ul className='mt-6 space-y-3 text-gray-600 dark:text-slate-400'>
                  <li className='flex items-center text-sm sm:text-base'>
                    <svg className='mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-500 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
                    </svg>
                    {t('Save time, boost productivity, and enhance creativity effortlessly;')}
                  </li>
                  <li className='flex items-center text-sm sm:text-base'>
                    <svg className='mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-500 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
                    </svg>
                    {t('Learn to create viral content in seconds;')}
                  </li>
                  <li className='flex items-center text-sm sm:text-base'>
                    <svg className='mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-500 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
                    </svg>
                    {t('Get your job done for you with ease;')}
                  </li>
                  <li className='flex items-center text-sm sm:text-base'>
                    <svg className='mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-500 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
                    </svg>
                    {t('Gain hands-on experience with advanced 300+ AI tools.')}
                  </li>
                </ul>

                {/* CTA buttons */}
                <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center'>
                  <a href={`#${WORKSHOP_CONTACT_ID}`} onClick={scrollToSection} className='inline-flex items-center justify-center rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-4 text-base font-bold shadow-sm hover:shadow transition-all duration-150 text-center'>
                    {t('Custom workshop')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* YouTube Video with clean bezel */}
          <div className='mt-20 sm:mt-24 md:mx-auto md:max-w-3xl lg:mx-0 lg:mt-0 lg:w-full lg:self-center'>
            <div className='relative'>
              <div className='relative aspect-video overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-lg dark:border-slate-800'>
                <iframe src='https://www.youtube.com/embed/nUX6DvUDsGg?si=l032443J8kssN8NS' title='YouTube video player' className='absolute top-0 left-0 w-full h-full' frameBorder={0} allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share' referrerPolicy='strict-origin-when-cross-origin' allowFullScreen/>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 5 Facts About Easy-Peasy.AI Stats Section */}
      <section className='bg-slate-50 py-16 dark:bg-slate-950 border-b border-slate-100 dark:border-slate-900'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-3xl text-center'>
            <h2 className='text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-500'>
              Why Learn from Easy-Peasy.AI?
            </h2>
            <p className='mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-4xl'>
              5 Facts About Easy-Peasy.AI
            </p>
          </div>
          <div className='mx-auto mt-12 grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5'>
            {stats.map((stat, idx) => (<div key={idx} className='flex flex-col justify-between rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 text-center transition-all duration-200 hover:shadow-md hover:scale-[1.02]'>
                <dt className='text-xs font-semibold leading-5 text-slate-500 dark:text-slate-400 order-last mt-2'>
                  {stat.label}
                  <span className='block text-[10px] font-normal text-slate-400 mt-1 leading-normal'>
                    {stat.description}
                  </span>
                </dt>
                <dd className='text-2xl font-extrabold tracking-tight text-indigo-600 dark:text-indigo-500'>
                  {stat.value}
                </dd>
              </div>))}
          </div>
        </div>
      </section>

      <UserPainWorkshop />
      <IsThisWorkshopForMe />
      <WorkshopFeatures />

      {/* Modernized "What Will Be Covered" Grid Section */}
      <section className='bg-white py-24 dark:bg-slate-900 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-3xl text-center'>
            <h2 className='text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-500'>
              The Curriculum
            </h2>
            <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100 sm:text-4xl'>
              {t('What Will Be Covered:')}
            </p>
            <p className='mt-4 text-lg text-gray-500 dark:text-slate-400'>
              Get hands-on training across these key areas, building direct,
              production-ready AI assets during the workshop.
            </p>
          </div>

          <div className='mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3'>
            {coverageItems.map((item, idx) => (<div key={idx} className='flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md dark:border-slate-800 dark:bg-slate-800/60'>
                <div>
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800'>
                    {item.icon}
                  </div>
                  <h3 className='mt-4 text-base font-bold text-gray-900 dark:text-slate-100'>
                    {item.title}
                  </h3>
                  <p className='mt-2 text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
                    {item.desc}
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      <BenefitsWorkshop />
      <TrainersSection />

      {/* Venue & Location Map Section */}
      <section className='bg-slate-50 py-24 dark:bg-slate-950 border-t border-slate-200/50 dark:border-slate-900/50'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid grid-cols-1 gap-x-12 gap-y-12 lg:grid-cols-2 items-center'>
            <div>
              <h2 className='text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-500'>
                Workshop Location
              </h2>

              <p className='mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400'>
                Our workshop takes place in a premium, fully-equipped seminar
                room in the heart of Singapore's central business district. Easy
                to access by MRT.
              </p>

              <div className='mt-8 space-y-6'>
                <div className='flex items-start gap-x-4'>
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-slate-50 text-indigo-600 dark:bg-slate-850 dark:text-indigo-400 flex-shrink-0'>
                    <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'/>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'/>
                    </svg>
                  </div>
                  <div>
                    <h4 className='text-sm font-bold text-slate-900 dark:text-slate-100'>
                      Address
                    </h4>
                    <p className='mt-1 text-sm text-slate-600 dark:text-slate-400'>
                      18 Cross Street, #02-101, JustCo, Singapore 048423
                    </p>
                  </div>
                </div>

                <div className='flex items-start gap-x-4'>
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-slate-50 text-indigo-600 dark:bg-slate-850 dark:text-indigo-400 flex-shrink-0'>
                    <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'/>
                    </svg>
                  </div>
                  <div>
                    <h4 className='text-sm font-bold text-slate-900 dark:text-slate-100'>
                      Access Times
                    </h4>
                    <p className='mt-1 text-sm text-slate-600 dark:text-slate-400'>
                      Sessions start exactly at the scheduled time. Doors open
                      15 minutes prior for check-in and seating.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className='relative h-96 w-full rounded-2xl overflow-hidden shadow-xl border border-slate-200 dark:border-slate-800'>
              <iframe src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.819!2d103.8470853!3d1.2835867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da19479f576053%3A0x16f24919e90571c4!2sEasy-Peasy.AI%20Pte.%20Ltd.!5e0!3m2!1sen!2ssg!4v1781136000000!5m2!1sen!2ssg' width='100%' height='100%' style={{ border: 0 }} allowFullScreen loading='lazy' referrerPolicy='no-referrer-when-downgrade' title='Google Map of Easy-Peasy.AI Pte. Ltd. at JustCo, Cross Street Exchange'/>
            </div>
          </div>
        </div>
      </section>

      <ImagesWithDetailsAboutUs />
      <ContactUsWorkshop />
      <CTAwithimagetilesWorkshop />
      <Testimonials />
      <FaqWorkshop />
      <Footer />
    </main>);
}
async function getStaticProps(): Promise<GetStaticPropsResult<Props>> {
    const products = await getActiveProductsWithPrices();
    return {
        props: {
            products,
        },
        revalidate: 60,
    };
}

    async function __Next_Translate__getStaticProps__1a0cbfb6643__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-workshops',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb6643__ as getStaticProps }
  